/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  LoadFrameworkCategories,
  LoadFrameworkDescendants,
  LoadFrameworkTagDescendants,
  LoadFrameworks,
  RemoveSelectedFrameworkFromStore,
} from '@app/app/page-modules/admin/store/framework.actions';
import { FrameWorkState } from '@app/app/page-modules/admin/store/framework.state';
import { ResourceTag } from '@app/app/shared/models';
import { Framework, FrameworkType, SkillTaxonomy } from '@app/app/shared/models/admin/admin.model';
import { Page } from '@app/app/shared/models/page';
import { EscoSkill } from '@app/app/shared/models/skill.model';
import { CORE_TAG_DATA_SERVICE, TagDataService } from '@app/app/shared/services/tags/tag-data.service';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { ArrowRight01Icon, Cancel01Icon, Search01Icon } from '@hugeicons/core-free-icons/index';
import { LucideArrowRight, LucideSearch, LucideX } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { Store } from '@ngxs/store';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

export interface AddFrameworkTagsV3DialogData {
  frameworkType: FrameworkType;
  existingTags?: ResourceTag[];
}

@Component({
  selector: 'ptl-add-framework-tags-dialog-v3',
  templateUrl: './add-framework-tags-dialog-v3.component.html',
  styleUrls: ['./add-framework-tags-dialog-v3.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatProgressSpinnerModule, MatTooltipModule, TranslocoModule, StyleguideModule],
})
export class AddFrameworkTagsDialogV3Component implements OnInit, OnDestroy {
  protected readonly Cancel01Icon = Cancel01Icon;
  protected readonly ArrowRight01Icon = ArrowRight01Icon;
  protected readonly Search01Icon = Search01Icon;

  frameworkType: FrameworkType = 'STANDARD';

  frameworks: Framework[] = [];
  skillTaxonomies: SkillTaxonomy[] = [];
  activeFrameworkId: string | null = null;
  frameworkTags: ResourceTag[] = [];
  nestedTags: ResourceTag[] | null = null;
  breadcrumbs: { id: string; title: string }[] = [];
  loadedTags: Record<string, ResourceTag[]> = {};
  selectedTags: ResourceTag[] = [];
  tagsLoading = false;
  dialogState: 'loading' | 'ready' | 'error' = 'loading';

  isEscoMode = false;
  escoSkills: EscoSkill[] = [];
  escoSearchLoading = false;
  escoSearchTerm = '';
  escoLoadingMore = false;
  escoHasMore = false;

  private escoSearchTerm$ = new Subject<string>();
  private escoCurrentPage = 0;
  private escoPageSize = 20;
  private existingTagIds = new Set<string>();
  private subscriptionEnd$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<AddFrameworkTagsDialogV3Component>,
    @Inject(MAT_DIALOG_DATA) private data: AddFrameworkTagsV3DialogData,
    private store: Store,
    private cdr: ChangeDetectorRef,
    @Inject(CORE_TAG_DATA_SERVICE) private tagDataService: TagDataService,
  ) {
    if (this.data?.frameworkType) {
      this.frameworkType = this.data.frameworkType;
    }
    if (this.data?.existingTags?.length) {
      this.existingTagIds = new Set(this.data.existingTags.map((t) => t._id));
    }
  }

  ngOnInit(): void {
    this.loadFrameworks();
    this.subscribeToFrameworks();
    this.subscribeToFrameworkTags();
    this.setupEscoSearch();
  }

  ngOnDestroy(): void {
    this.subscriptionEnd$.next();
    this.subscriptionEnd$.complete();
    this.store.dispatch(new RemoveSelectedFrameworkFromStore());
  }

  get displayedTags(): ResourceTag[] {
    return this.nestedTags !== null ? this.nestedTags : this.frameworkTags;
  }

  get isNestedLevel(): boolean {
    return this.nestedTags !== null;
  }

  get activeFrameworkTitle(): string {
    const item = this.allItems.find((i) => i.id === this.activeFrameworkId);
    return item?.title || '';
  }

  get allItems(): { id: string; title: string; type: 'taxonomy' | 'framework' }[] {
    const items: { id: string; title: string; type: 'taxonomy' | 'framework' }[] = [];
    for (const f of this.frameworks) {
      items.push({ id: f.uid || f._id, title: f.title, type: 'framework' });
    }
    for (const t of this.skillTaxonomies) {
      items.push({ id: t.frameworkId, title: t.title, type: 'taxonomy' });
    }
    return items;
  }

  onSelectFramework(frameworkId: string): void {
    if (this.activeFrameworkId === frameworkId && !this.isNestedLevel && !this.isEscoMode) {
      return;
    }

    const item = this.allItems.find((i) => i.id === frameworkId);
    this.activeFrameworkId = frameworkId;
    this.resetTagsHierarchy();

    if (item?.type === 'taxonomy') {
      this.isEscoMode = true;
      this.escoSkills = [];
      this.escoSearchTerm = '';
      this.escoSearchLoading = false;
      this.escoCurrentPage = 0;
      this.escoHasMore = false;
      this.tagsLoading = false;
    } else {
      this.isEscoMode = false;
      this.tagsLoading = true;
      this.store.dispatch(new LoadFrameworkDescendants(frameworkId));
    }
  }

  onSelectTag(tag: ResourceTag): void {
    const index = this.selectedTags.findIndex((t) => t._id === tag._id);
    if (index === -1) {
      this.selectedTags = [...this.selectedTags, tag];
    } else {
      this.selectedTags = this.selectedTags.filter((t) => t._id !== tag._id);
    }
  }

  isTagSelected(tag: ResourceTag): boolean {
    return this.selectedTags.some((t) => t._id === tag._id);
  }

  isTagExisting(tag: ResourceTag): boolean {
    return this.existingTagIds.has(tag._id);
  }

  onRemoveSelectedTag(tag: ResourceTag): void {
    this.selectedTags = this.selectedTags.filter((t) => t._id !== tag._id);
  }

  onLoadNestedTags(tag: ResourceTag): void {
    if (!tag.descendantsCount) {
      return;
    }
    this.tagsLoading = true;
    this.store
      .dispatch(new LoadFrameworkTagDescendants(this.activeFrameworkId, tag._id))
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe(() => {
        this.store
          .select(FrameWorkState.tagDescendants)
          .pipe(
            filter(({ loading, data }) => !loading && !!data),
            map(({ data }) => data),
            takeUntil(this.subscriptionEnd$),
          )
          .subscribe((tags) => {
            if (tags.length) {
              this.loadedTags[tag._id] = tags;
              this.nestedTags = tags;
              this.breadcrumbs = [...this.breadcrumbs, { id: tag._id, title: tag.title }];
            }
            this.tagsLoading = false;
            this.cdr.markForCheck();
          })
          .unsubscribe();
      });
  }

  onBreadcrumbClick(index: number): void {
    if (index < 0) {
      // Root level
      this.breadcrumbs = [];
      this.nestedTags = null;
    } else {
      this.breadcrumbs = this.breadcrumbs.slice(0, index + 1);
      const tagId = this.breadcrumbs[index].id;
      this.nestedTags = this.loadedTags[tagId] || null;
    }
  }

  onEscoSearchInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.escoSearchTerm = term;
    this.escoSearchTerm$.next(term);
  }

  onSelectEscoResult(result: EscoSkill): void {
    const tagId = result.tagUid || result.conceptUri;
    const index = this.selectedTags.findIndex((t) => t.escoConceptUri === result.conceptUri);
    if (index === -1) {
      this.selectedTags = [
        ...this.selectedTags,
        {
          _id: tagId,
          title: result.preferredLabel,
          escoConceptUri: result.conceptUri,
        },
      ];
    } else {
      this.selectedTags = this.selectedTags.filter((t) => t.escoConceptUri !== result.conceptUri);
    }
  }

  isEscoResultSelected(result: EscoSkill): boolean {
    return this.selectedTags.some((t) => t.escoConceptUri === result.conceptUri);
  }

  isEscoResultExisting(result: EscoSkill): boolean {
    return result.usedInOrganization && !!result.tagUid && this.existingTagIds.has(result.tagUid);
  }

  onEscoTagsScroll(event: Event): void {
    if (!this.escoHasMore || this.escoLoadingMore) {
      return;
    }
    const el = event.target as HTMLElement;
    const threshold = 50;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      this.loadMoreEscoResults();
    }
  }

  onAddTags(): void {
    const escoTags = this.selectedTags.filter((t) => t.escoConceptUri);
    if (escoTags.length) {
      const conceptUris = escoTags.map((t) => t.escoConceptUri);
      this.tagsLoading = true;
      this.cdr.markForCheck();
      this.tagDataService
        .resolveEscoConcepts(conceptUris)
        .pipe(take(1))
        .subscribe((result) => {
          if (result.isSuccess && result.value) {
            const resolvedMap = new Map(result.value.map((r) => [r.conceptUri, r]));
            const resolvedTags = this.selectedTags.map((tag) => {
              if (tag.escoConceptUri && resolvedMap.has(tag.escoConceptUri)) {
                const resolved = resolvedMap.get(tag.escoConceptUri);
                return { ...tag, _id: resolved.tagUid, frameworkId: this.activeFrameworkId };
              }
              return { ...tag, frameworkId: this.activeFrameworkId };
            });
            this.dialogRef.close(resolvedTags);
          } else {
            // Fallback: close with unresolved tags
            const fallbackTags = this.selectedTags.map((tag) => ({ ...tag, frameworkId: this.activeFrameworkId }));
            this.dialogRef.close(fallbackTags);
          }
          this.tagsLoading = false;
        });
    } else {
      const result = this.selectedTags.map((tag) => ({
        ...tag,
        frameworkId: this.activeFrameworkId,
      }));
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  onClose(): void {
    this.dialogRef.close(undefined);
  }

  private loadFrameworks(): void {
    if (this.frameworkType === 'CATEGORIES') {
      this.store.dispatch(new LoadFrameworkCategories());
    } else {
      this.store.dispatch(new LoadFrameworks());
    }
  }

  private subscribeToFrameworks(): void {
    this.store
      .select(FrameWorkState.frameworks)
      .pipe(
        takeUntil(this.subscriptionEnd$),
        filter(({ loading, data }) => !loading && !!data),
        map(({ data }) => data),
      )
      .subscribe((frameworks) => {
        this.frameworks = frameworks;
        this.dialogState = 'ready';

        if (this.frameworkType !== 'CATEGORIES') {
          this.subscribeToSkillTaxonomies(frameworks);
        } else {
          this.autoSelectFirstItem();
        }

        this.cdr.markForCheck();
      });

    // Also handle error
    this.store
      .select(FrameWorkState.frameworks)
      .pipe(
        takeUntil(this.subscriptionEnd$),
        filter(({ error }) => !!error),
      )
      .subscribe(() => {
        this.dialogState = 'error';
        this.cdr.markForCheck();
      });
  }

  private subscribeToSkillTaxonomies(frameworks: Framework[]): void {
    this.store
      .select(FrameWorkState.enabledSkillTaxonomies)
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe((taxonomies) => {
        this.skillTaxonomies = taxonomies || [];
        if (this.frameworks.length || this.skillTaxonomies.length) {
          this.autoSelectFirstItem();
        }
        this.cdr.markForCheck();
      });
  }

  private autoSelectFirstItem(): void {
    if (this.activeFrameworkId) {
      return;
    }
    const items = this.allItems;
    if (items.length > 0) {
      this.onSelectFramework(items[0].id);
    }
  }

  private subscribeToFrameworkTags(): void {
    this.store
      .select(FrameWorkState.frameworkDescendants)
      .pipe(
        takeUntil(this.subscriptionEnd$),
        filter(({ loading, data }) => !loading && !!data),
        map(({ data }) => data),
      )
      .subscribe((tags) => {
        this.frameworkTags = tags;
        this.tagsLoading = false;
        this.cdr.markForCheck();
      });
  }

  private setupEscoSearch(): void {
    this.escoSearchTerm$
      .pipe(
        takeUntil(this.subscriptionEnd$),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (!term || term.length < 2) {
            this.escoSkills = [];
            this.escoSearchLoading = false;
            this.escoCurrentPage = 0;
            this.escoHasMore = false;
            this.cdr.markForCheck();
            return of(null);
          }
          this.escoSearchLoading = true;
          this.escoCurrentPage = 0;
          this.cdr.markForCheck();
          return this.tagDataService.searchEscoSkills(term, 0, this.escoPageSize);
        }),
      )
      .subscribe((result) => {
        if (result && result.value) {
          this.applyEscoPage(result.value, false);
        }
        this.escoSearchLoading = false;
        this.cdr.markForCheck();
      });
  }

  private loadMoreEscoResults(): void {
    this.escoLoadingMore = true;
    this.escoCurrentPage++;
    this.cdr.markForCheck();
    this.tagDataService
      .searchEscoSkills(this.escoSearchTerm, this.escoCurrentPage, this.escoPageSize)
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe((result) => {
        if (result && result.value) {
          this.applyEscoPage(result.value, true);
        }
        this.escoLoadingMore = false;
        this.cdr.markForCheck();
      });
  }

  private applyEscoPage(page: Page<EscoSkill>, append: boolean): void {
    this.escoSkills = append ? [...this.escoSkills, ...page.content] : page.content;
    this.escoHasMore =
      page.numberOfPages != null ? page.currentPageNumber < page.numberOfPages - 1 : page.content.length >= this.escoPageSize;
  }

  private resetTagsHierarchy(): void {
    this.nestedTags = null;
    this.breadcrumbs = [];
    this.loadedTags = {};
  }

  protected readonly LucideSearch = LucideSearch;
  protected readonly LucideX = LucideX;
  protected readonly LucideArrowRight = LucideArrowRight;
}
